import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ShoppingCart, Trash2, Plus, Minus, ArrowRight, ArrowLeft, MapPin, Upload, FileText, CheckCircle2, AlertCircle, X, Loader2, Share2, Copy, Mail, TrendingDown, Edit3, Calendar, Phone, FileUp } from 'lucide-react';
import { useCart } from '@/lib/cartContext';
import { estimateMonthlySavings, formatINRCompact } from '@/lib/savings';
import { toast } from 'sonner';
import { trackEvent, EVENTS } from '@/lib/analytics';
import { trackViewQuoteCart, trackStartRfq, trackSubmitRfq } from '@/lib/ga4';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 1, label: 'Cart', icon: ShoppingCart },
  { id: 2, label: 'Site & Documents', icon: MapPin },
  { id: 3, label: 'Review & Submit', icon: CheckCircle2 },
];

function formatBytes(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

export default function CartPage() {
  const { cart, removeItem, updateItemMode, updateItemQuantity, addSite, updateSite, clearCart, copySiteItems, itemCount, allBuyEligible, hasRentItems } = useCart();
  const [step, setStep] = useState(1);
  const [contact, setContact] = useState({ name: '', company: '', email: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [rfqResult, setRfqResult] = useState(null);
  const [siteFiles, setSiteFiles] = useState({});
  const [uploading, setUploading] = useState({});
  const [editingSiteName, setEditingSiteName] = useState(null);
  const [postUploadFiles, setPostUploadFiles] = useState([]);
  const [postUploading, setPostUploading] = useState(false);
  const fileInputRefs = useRef({});
  const postUploadRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    trackViewQuoteCart(cart.items.length, cart.sites.length);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const formatINR = (n) => n ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n) : '—';

  const handleFileSelect = async (siteId, files) => {
    if (!files || files.length === 0) return;
    setUploading(prev => ({ ...prev, [siteId]: true }));
    const uploaded = [];
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('site_id', siteId);
      try {
        const res = await fetch(`${BACKEND_URL}/api/upload`, { method: 'POST', body: formData });
        const data = await res.json();
        if (data.error) { toast.error(data.error); continue; }
        uploaded.push(data);
        trackEvent('upload_document', { site_id: siteId, file_type: file.name.split('.').pop() });
      } catch { toast.error(`Failed to upload ${file.name}`); }
    }
    if (uploaded.length > 0) {
      setSiteFiles(prev => ({ ...prev, [siteId]: [...(prev[siteId] || []), ...uploaded] }));
      toast.success(`${uploaded.length} file(s) uploaded`);
    }
    setUploading(prev => ({ ...prev, [siteId]: false }));
  };

  const handleFileRemove = async (siteId, fileId) => {
    try {
      await fetch(`${BACKEND_URL}/api/upload/${fileId}`, { method: 'DELETE' });
      setSiteFiles(prev => ({ ...prev, [siteId]: (prev[siteId] || []).filter(f => f.file_id !== fileId) }));
      toast.success('File removed');
    } catch { toast.error('Failed to remove file'); }
  };

  const buyTotal = cart.items.filter(i => i.mode === 'buy').reduce((sum, i) => sum + ((i.buy_from_price_ex_gst || i.battery_kwh * 11500) * i.quantity), 0);
  const rentTotal = cart.items.filter(i => i.mode === 'rent').reduce((sum, i) => sum + ((i.rent_from_inr_per_month || i.battery_kwh * 8.5) * i.quantity), 0);

  const groupedBySite = {};
  cart.sites.forEach(site => { groupedBySite[site.id] = { site, items: cart.items.filter(i => i.siteId === site.id) }; });

  const handleSubmit = async (submitMode) => {
    if (!contact.name || !contact.email) { toast.error('Please provide name and email.'); return; }
    setSubmitting(true);
    trackEvent(submitMode === 'checkout' ? 'start_checkout' : EVENTS.SUBMIT_RFQ, { items: cart.items.length });
    trackStartRfq('cart', cart.sites.length);
    try {
      // Build cart snapshot grouped by site
      const cartSnapshot = {
        items: cart.items.map(i => ({
          sku: i.sku,
          product_name: i.product_name,
          mode: i.mode,
          quantity: i.quantity,
          pcs_kw: i.pcs_kw,
          battery_kwh: i.battery_kwh,
          siteId: i.siteId,
        })),
        totals: {
          buy_total: buyTotal,
          rent_total_monthly: rentTotal,
        },
      };

      // Build savings profile from total kWh
      const totalKwh = cart.items.reduce((sum, i) => sum + i.battery_kwh * i.quantity, 0);
      const savings = estimateMonthlySavings(totalKwh);
      const savingsProfile = {
        total_kwh: totalKwh,
        estimated_monthly_savings_low: savings.low,
        estimated_monthly_savings_high: savings.high,
      };

      // Build sites array
      const sites = cart.sites.map((s, idx) => ({
        site_index: idx,
        site_label: s.name || `Site ${idx + 1}`,
        sector: s.sector || null,
        city: s.city || null,
        address: s.address || null,
        use_case_flags: {},
        quick_inputs: {},
        uploaded_file_ids: (siteFiles[s.id] || []).map(f => f.file_id),
      }));

      const res = await fetch(`${BACKEND_URL}/api/rfq/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: contact.company,
          contact_name: contact.name,
          email: contact.email,
          phone: contact.phone,
          notes: '',
          source: 'website',
          cart_snapshot: cartSnapshot,
          savings_profile: savingsProfile,
          sites,
        }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Submission failed'); return; }
      setRfqResult(data);
      setSubmitted(true);
      const docsUploaded = Object.values(siteFiles).some(f => f.length > 0);
      trackSubmitRfq(data.rfq_id, cart.sites.length, docsUploaded);
      clearCart();
    } catch { toast.error('Submission failed. Please try again.'); }
    finally { setSubmitting(false); }
  };

  const handlePostUpload = async (files) => {
    if (!files || files.length === 0 || !rfqResult) return;
    setPostUploading(true);
    const uploaded = [];
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_type', 'OTHER');
      try {
        const res = await fetch(`${BACKEND_URL}/api/rfq/${rfqResult.rfq_id}/upload`, { method: 'POST', body: formData });
        const data = await res.json();
        if (data.error) { toast.error(data.error); continue; }
        uploaded.push(data);
      } catch { toast.error(`Failed to upload ${file.name}`); }
    }
    if (uploaded.length > 0) {
      setPostUploadFiles(prev => [...prev, ...uploaded]);
      toast.success(`${uploaded.length} file(s) uploaded successfully`);
    }
    setPostUploading(false);
  };

  if (submitted && rfqResult) {
    return (
      <div className="py-12 lg:py-20" data-testid="cart-success">
        <div className="voltide-container max-w-2xl animate-fade-up">
          {/* Success header */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-emerald-500" />
            </div>
            <h1 className="text-3xl font-bold text-[#1A1A1A] mb-3">Thanks — we've received your request</h1>
            <p className="text-[#57534E] leading-relaxed">Our team will review your requirements and prepare a formal proposal.</p>
          </div>

          {/* RFQ Reference Card */}
          <div className="bento-card p-6 mb-6" data-testid="rfq-reference-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs text-[#A8A29E] uppercase tracking-wider">RFQ Reference</p>
                <p className="mono text-2xl font-bold text-[#0061DD]" data-testid="rfq-ref-id">{rfqResult.rfq_ref}</p>
              </div>
              <Badge className="rounded-full bg-blue-50 text-[#0061DD] border-blue-100 text-xs">
                {rfqResult.status}
              </Badge>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-[#A8A29E]">Sites</span>
                <p className="font-medium text-[#1A1A1A]">{rfqResult.sites_created} site{rfqResult.sites_created !== 1 ? 's' : ''}</p>
              </div>
              <div>
                <span className="text-[#A8A29E]">Email confirmation</span>
                <p className="font-medium text-[#1A1A1A]">{rfqResult.email_status === 'SENT' ? 'Sent' : 'Queued'}</p>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="bento-card p-6 mb-6" data-testid="rfq-next-steps">
            <h2 className="text-lg font-bold text-[#1A1A1A] mb-4">What happens next?</h2>
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                  <span className="text-sm font-bold text-[#0061DD]">1</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#1A1A1A]">Review</p>
                  <p className="text-xs text-[#57534E]">Our solutions team reviews your requirements (1-2 business days)</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                  <span className="text-sm font-bold text-[#0061DD]">2</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#1A1A1A]">Site Assessment</p>
                  <p className="text-xs text-[#57534E]">We may reach out for a brief site visit or virtual walkthrough</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                  <span className="text-sm font-bold text-[#0061DD]">3</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#1A1A1A]">Formal Proposal</p>
                  <p className="text-xs text-[#57534E]">You'll receive a detailed quote with pricing, timeline, and installation plan</p>
                </div>
              </div>
            </div>
          </div>

          {/* Upload More Documents */}
          <div className="bento-card p-6 mb-6" data-testid="rfq-upload-more">
            <div className="flex items-center gap-2 mb-3">
              <FileUp className="w-5 h-5 text-[#0061DD]" />
              <h2 className="text-lg font-bold text-[#1A1A1A]">Upload more documents</h2>
            </div>
            <p className="text-xs text-[#57534E] mb-4">Help us prepare a more accurate quote by sharing electricity bills, DG logs, or solar data.</p>

            {postUploadFiles.length > 0 && (
              <div className="space-y-2 mb-4">
                {postUploadFiles.map(f => (
                  <div key={f.upload_id} className="flex items-center gap-2 p-2 rounded-lg bg-emerald-50">
                    <FileText className="w-4 h-4 text-emerald-600" />
                    <span className="text-xs font-medium text-emerald-700 flex-1 truncate">{f.file_name}</span>
                    <Badge className="rounded-full bg-emerald-100 text-emerald-700 text-[10px]">{f.file_type}</Badge>
                  </div>
                ))}
              </div>
            )}

            <input
              ref={postUploadRef}
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.csv,.doc,.docx"
              className="hidden"
              onChange={(e) => handlePostUpload(e.target.files)}
            />
            <Button
              variant="outline"
              className="rounded-full gap-2 w-full"
              onClick={() => postUploadRef.current?.click()}
              disabled={postUploading}
              data-testid="upload-more-btn"
            >
              {postUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {postUploading ? 'Uploading...' : 'Choose Files to Upload'}
            </Button>
            <p className="text-[10px] text-[#A8A29E] mt-2 text-center">PDF, images, Excel, CSV, Word — max 10 MB each</p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3">
            <Link to="/products">
              <Button className="w-full rounded-full bg-[#0061DD] hover:bg-[#004BB3] text-white gap-2" data-testid="continue-shopping-btn">
                Back to Products <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="w-full rounded-full">Back to Home</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (itemCount === 0) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="cart-empty">
        <div className="text-center max-w-md mx-auto px-4">
          <ShoppingCart className="w-16 h-16 text-[#A8A29E] mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#1A1A1A] mb-2">Your Quote Cart is empty</h1>
          <p className="text-[#57534E] mb-6">Add products from our catalogue or get a custom recommendation.</p>
          <div className="flex flex-col gap-3">
            <Link to="/products"><Button className="w-full rounded-full bg-[#0061DD] text-white" data-testid="browse-products-btn">Browse Products</Button></Link>
            <Link to="/quote"><Button variant="outline" className="w-full rounded-full border-[#0061DD] text-[#0061DD]" data-testid="get-recommendation-btn">Get a Recommendation</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="cart-page">
      <section className="py-12 lg:py-16">
        <div className="voltide-container max-w-4xl">
          {/* Stepper */}
          <div className="flex items-center justify-center gap-2 mb-10" data-testid="cart-stepper">
            {STEPS.map((s, i) => (
              <div key={s.id} className="flex items-center">
                <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${step === s.id ? 'bg-[#0061DD] text-white shadow-md' : step > s.id ? 'bg-emerald-50 text-emerald-700' : 'bg-stone-100 text-[#A8A29E]'}`}>
                  <s.icon className="w-4 h-4" />
                  {s.label}
                </div>
                {i < STEPS.length - 1 && <div className="w-8 h-0.5 bg-stone-200 mx-1" />}
              </div>
            ))}
          </div>

          {/* Step 1: Cart */}
          {step === 1 && (
            <div data-testid="cart-step-1">
              <h1 className="text-2xl font-bold text-[#1A1A1A] mb-1">Quote Cart</h1>
              <p className="text-sm text-[#57534E] mb-6">Build your solution. Rentals are quote-based; standard purchases may be checkout-enabled.</p>

              {Object.entries(groupedBySite).map(([siteId, { site, items }]) => (
                <div key={siteId} className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    {editingSiteName === siteId ? (
                      <input
                        autoFocus
                        value={site.name}
                        onChange={e => updateSite(siteId, { name: e.target.value })}
                        onBlur={() => setEditingSiteName(null)}
                        onKeyDown={e => e.key === 'Enter' && setEditingSiteName(null)}
                        className="text-sm font-semibold text-[#57534E] uppercase tracking-wider border-b border-[#0061DD] outline-none bg-transparent"
                        data-testid={`site-name-input-${siteId}`}
                      />
                    ) : (
                      <h3 className="text-sm font-semibold text-[#57534E] uppercase tracking-wider">{site.name}</h3>
                    )}
                    <button
                      onClick={() => setEditingSiteName(editingSiteName === siteId ? null : siteId)}
                      className="text-[#A8A29E] hover:text-[#0061DD]"
                      data-testid={`rename-site-${siteId}`}
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    {cart.sites.length > 1 && (
                      <span className="text-xs text-[#A8A29E] ml-auto">
                        {items.reduce((sum, i) => sum + i.battery_kwh * i.quantity, 0)} kWh total
                      </span>
                    )}
                  </div>
                  {items.length === 0 ? (
                    <p className="text-sm text-[#A8A29E] p-4 border border-dashed border-stone-200 rounded-xl text-center">No items for this site. Browse products to add.</p>
                  ) : (
                    <div className="space-y-3">
                      {items.map(item => (
                        <div key={`${item.sku}-${siteId}`} className="bento-card p-4 flex gap-4" data-testid={`cart-item-${item.sku}`}>
                          <img src={item.image} alt={item.product_name} className="w-20 h-20 object-contain rounded-lg bg-[#F9F9F7] p-2 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-bold text-[#1A1A1A] truncate">{item.product_name}</h4>
                            <p className="mono text-xs text-[#57534E]">{item.pcs_kw} kW / {item.battery_kwh} kWh</p>
                            <div className="flex gap-2 mt-2">
                              <button onClick={() => updateItemMode(item.sku, siteId, 'buy')} className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${item.mode === 'buy' ? 'bg-[#0061DD] text-white' : 'bg-stone-100 text-[#57534E]'}`} data-testid={`mode-buy-${item.sku}`}>
                                Buy (standard)
                              </button>
                              <button onClick={() => updateItemMode(item.sku, siteId, 'rent')} className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${item.mode === 'rent' ? 'bg-[#0061DD] text-white' : 'bg-stone-100 text-[#57534E]'}`} data-testid={`mode-rent-${item.sku}`}>
                                Rent (quote-based)
                              </button>
                              {item.mode === 'rent' && <Badge className="rounded-full bg-amber-50 text-amber-700 text-xs border-amber-100">RFQ-first</Badge>}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2 shrink-0">
                            <button onClick={() => removeItem(item.sku, siteId)} className="text-[#A8A29E] hover:text-red-500" data-testid={`remove-${item.sku}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                            <div className="flex items-center gap-2">
                              <button onClick={() => updateItemQuantity(item.sku, siteId, item.quantity - 1)} className="w-7 h-7 rounded-full bg-stone-100 flex items-center justify-center hover:bg-stone-200"><Minus className="w-3 h-3" /></button>
                              <span className="mono text-sm font-semibold w-6 text-center">{item.quantity}</span>
                              <button onClick={() => updateItemQuantity(item.sku, siteId, item.quantity + 1)} className="w-7 h-7 rounded-full bg-stone-100 flex items-center justify-center hover:bg-stone-200"><Plus className="w-3 h-3" /></button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              <button onClick={addSite} className="text-sm text-[#0061DD] hover:underline flex items-center gap-1 mb-6" data-testid="add-site-btn">
                <Plus className="w-4 h-4" /> Add another site
              </button>

              {/* Estimated Total */}
              <div className="bento-card p-5 mb-4" data-testid="cart-totals">
                <h3 className="text-sm font-semibold text-[#1A1A1A] mb-3">Estimated Total</h3>
                {buyTotal > 0 && (
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-[#57534E]">Buy items (indicative)</span>
                    <span className="mono font-semibold">{formatINR(buyTotal)}</span>
                  </div>
                )}
                {rentTotal > 0 && (
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-[#57534E]">Rent items (estimate/mo)</span>
                    <span className="mono font-semibold text-[#0061DD]">{formatINR(rentTotal)}/mo</span>
                  </div>
                )}
                {hasRentItems && <p className="text-xs text-[#A8A29E] mt-2">Rent estimates are indicative. Final pricing in proposal after site assessment.</p>}
                <p className="text-xs text-[#A8A29E] mt-2">Prices are indicative and subject to change based on site conditions and final scope.</p>
              </div>

              {/* Monthly Impact Panel */}
              {cart.items.length > 0 && (
                <div className="bento-card p-5 mb-6 bg-emerald-50/30 border border-emerald-100" data-testid="monthly-impact-panel">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingDown className="w-5 h-5 text-emerald-600" />
                    <h3 className="text-sm font-semibold text-[#1A1A1A]">Estimated Monthly Impact</h3>
                  </div>
                  {(() => {
                    const totalKwh = cart.items.reduce((sum, i) => sum + i.battery_kwh * i.quantity, 0);
                    const savings = estimateMonthlySavings(totalKwh);
                    const netLow = savings.low - rentTotal;
                    const netHigh = savings.high - rentTotal;
                    return (
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-[#57534E]">DG cost savings</span>
                          <span className="mono font-semibold text-emerald-700">+{formatINRCompact(savings.low)} to +{formatINRCompact(savings.high)}/mo</span>
                        </div>
                        {rentTotal > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-[#57534E]">Rent cost</span>
                            <span className="mono font-semibold text-red-600">-{formatINRCompact(rentTotal)}/mo</span>
                          </div>
                        )}
                        {rentTotal > 0 && (
                          <div className="flex justify-between text-sm pt-2 border-t border-emerald-200">
                            <span className="font-semibold text-[#1A1A1A]">Net monthly impact</span>
                            <span className={`mono font-bold ${netLow >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                              {netLow >= 0 ? '+' : ''}{formatINRCompact(netLow)} to {netHigh >= 0 ? '+' : ''}{formatINRCompact(netHigh)}/mo
                            </span>
                          </div>
                        )}
                        <p className="text-xs text-emerald-600 mt-1">Based on {totalKwh} kWh total capacity vs diesel generator costs</p>
                      </div>
                    );
                  })()}
                </div>
              )}

              <Button onClick={() => setStep(2)} className="rounded-full bg-[#0061DD] hover:bg-[#004BB3] text-white px-8 py-5 text-lg font-semibold gap-2 w-full" data-testid="cart-continue-btn">
                Continue <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
          )}

          {/* Step 2: Site & Documents */}
          {step === 2 && (
            <div data-testid="cart-step-2">
              <h1 className="text-2xl font-bold text-[#1A1A1A] mb-1">Site details</h1>
              <p className="text-sm text-[#57534E] mb-6">Uploading bills/DG logs helps us deliver a faster, tighter quote.</p>

              {cart.sites.map((site, idx) => (
                <div key={site.id} className="bento-card p-6 mb-6" data-testid={`site-form-${idx}`}>
                  <h3 className="text-lg font-bold text-[#1A1A1A] mb-4">{site.name}</h3>
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <Label className="text-sm font-medium">Address</Label>
                      <Input value={site.address} onChange={e => updateSite(site.id, { address: e.target.value })} placeholder="Site address" className="mt-1.5 h-11 rounded-xl" data-testid={`site-address-${idx}`} />
                    </div>
                    <div>
                      <Label className="text-sm font-medium">City</Label>
                      <Input value={site.city} onChange={e => updateSite(site.id, { city: e.target.value })} placeholder="City" className="mt-1.5 h-11 rounded-xl" data-testid={`site-city-${idx}`} />
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Contact Person</Label>
                      <Input value={site.contact} onChange={e => updateSite(site.id, { contact: e.target.value })} placeholder="Contact name" className="mt-1.5 h-11 rounded-xl" data-testid={`site-contact-${idx}`} />
                    </div>
                  </div>
                  <div>
                      <Label className="text-sm font-medium mb-2 block">Documents (optional)</Label>
                      <input
                        ref={el => fileInputRefs.current[site.id] = el}
                        type="file"
                        multiple
                        accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.csv,.doc,.docx"
                        className="hidden"
                        onChange={e => handleFileSelect(site.id, Array.from(e.target.files))}
                        data-testid={`site-file-input-${idx}`}
                      />
                      <div
                        className="p-5 rounded-xl border-2 border-dashed border-stone-300 text-center hover:border-[#0061DD] hover:bg-blue-50/30 transition-colors cursor-pointer"
                        onClick={() => fileInputRefs.current[site.id]?.click()}
                        onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-[#0061DD]', 'bg-blue-50/30'); }}
                        onDragLeave={e => { e.currentTarget.classList.remove('border-[#0061DD]', 'bg-blue-50/30'); }}
                        onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('border-[#0061DD]', 'bg-blue-50/30'); handleFileSelect(site.id, Array.from(e.dataTransfer.files)); }}
                        data-testid={`site-dropzone-${idx}`}
                      >
                        {uploading[site.id] ? (
                          <Loader2 className="w-6 h-6 text-[#0061DD] mx-auto mb-2 animate-spin" />
                        ) : (
                          <Upload className="w-6 h-6 text-[#A8A29E] mx-auto mb-2" />
                        )}
                        <p className="text-sm text-[#57534E]">
                          {uploading[site.id] ? 'Uploading...' : 'Drop electricity bills & DG logs here, or click to browse'}
                        </p>
                        <p className="text-xs text-[#A8A29E] mt-1">PDF, images, Excel, CSV up to 10 MB each</p>
                      </div>
                      {(siteFiles[site.id] || []).length > 0 && (
                        <div className="mt-3 space-y-2" data-testid={`site-files-${idx}`}>
                          {siteFiles[site.id].map(file => (
                            <div key={file.file_id} className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                              <FileText className="w-4 h-4 text-[#0061DD] shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-[#1A1A1A] truncate">{file.original_name}</p>
                                <p className="text-xs text-[#A8A29E]">{formatBytes(file.size_bytes)}</p>
                              </div>
                              <button
                                onClick={e => { e.stopPropagation(); handleFileRemove(site.id, file.file_id); }}
                                className="text-[#A8A29E] hover:text-red-500 shrink-0"
                                data-testid={`remove-file-${file.file_id}`}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                </div>
              ))}

              <div className="flex gap-3">
                <Button onClick={() => setStep(1)} variant="outline" className="rounded-full gap-2" data-testid="cart-back-step1-btn"><ArrowLeft className="w-4 h-4" /> Back</Button>
                <Button onClick={() => setStep(3)} className="flex-1 rounded-full bg-[#0061DD] hover:bg-[#004BB3] text-white px-8 py-5 text-lg font-semibold gap-2" data-testid="cart-review-btn">
                  Review <ArrowRight className="w-5 h-5" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Review & Submit */}
          {step === 3 && (
            <div data-testid="cart-step-3">
              <h1 className="text-2xl font-bold text-[#1A1A1A] mb-1">Review & submit</h1>
              <p className="text-sm text-[#57534E] mb-6">Confirm your selections before submitting.</p>

              {/* Contact */}
              <div className="bento-card p-6 mb-6">
                <h3 className="text-lg font-bold text-[#1A1A1A] mb-4">Your Contact Details</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label className="text-sm font-medium">Name *</Label><Input value={contact.name} onChange={e => setContact(p => ({ ...p, name: e.target.value }))} className="mt-1.5 h-11 rounded-xl" data-testid="review-name" required /></div>
                  <div><Label className="text-sm font-medium">Company</Label><Input value={contact.company} onChange={e => setContact(p => ({ ...p, company: e.target.value }))} className="mt-1.5 h-11 rounded-xl" data-testid="review-company" /></div>
                  <div><Label className="text-sm font-medium">Email *</Label><Input type="email" value={contact.email} onChange={e => setContact(p => ({ ...p, email: e.target.value }))} className="mt-1.5 h-11 rounded-xl" data-testid="review-email" required /></div>
                  <div><Label className="text-sm font-medium">Phone</Label><Input value={contact.phone} onChange={e => setContact(p => ({ ...p, phone: e.target.value }))} className="mt-1.5 h-11 rounded-xl" data-testid="review-phone" /></div>
                </div>
              </div>

              {/* Items Summary */}
              <div className="bento-card p-6 mb-6" data-testid="review-items">
                <h3 className="text-lg font-bold text-[#1A1A1A] mb-4">Selected Items</h3>
                {cart.items.map(item => (
                  <div key={`${item.sku}-${item.siteId}`} className="flex items-center gap-3 py-3 border-b border-stone-100 last:border-0">
                    <img src={item.image} alt={item.product_name} className="w-12 h-12 object-contain rounded bg-[#F9F9F7] p-1" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-[#1A1A1A]">{item.product_name} x{item.quantity}</p>
                      <p className="text-xs text-[#57534E]">{item.mode === 'buy' ? 'Buy (standard)' : 'Rent (quote-based)'}</p>
                    </div>
                    <Badge className={`rounded-full text-xs ${item.mode === 'rent' ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>
                      {item.mode === 'rent' ? 'RFQ' : 'Buy'}
                    </Badge>
                  </div>
                ))}
              </div>

              {/* Pricing Summary */}
              <div className="bento-card p-6 mb-6" data-testid="review-pricing">
                <h3 className="text-lg font-bold text-[#1A1A1A] mb-3">Price Summary</h3>
                {buyTotal > 0 && <div className="flex justify-between text-sm mb-1"><span>Buy total (indicative)</span><span className="mono font-semibold">{formatINR(buyTotal)}</span></div>}
                {rentTotal > 0 && <div className="flex justify-between text-sm mb-1"><span>Rent estimate/mo</span><span className="mono font-semibold text-[#0061DD]">{formatINR(rentTotal)}</span></div>}
              </div>

              {/* Uploaded Documents */}
              {Object.values(siteFiles).some(f => f.length > 0) && (
                <div className="bento-card p-6 mb-6" data-testid="review-documents">
                  <h3 className="text-lg font-bold text-[#1A1A1A] mb-3">Uploaded Documents</h3>
                  {cart.sites.map((site, idx) => (
                    (siteFiles[site.id] || []).length > 0 && (
                      <div key={site.id} className="mb-3 last:mb-0">
                        <p className="text-xs font-semibold text-[#57534E] uppercase tracking-wider mb-2">{site.name}</p>
                        {siteFiles[site.id].map(file => (
                          <div key={file.file_id} className="flex items-center gap-2 py-1.5">
                            <FileText className="w-3.5 h-3.5 text-[#0061DD]" />
                            <span className="text-sm text-[#1A1A1A]">{file.original_name}</span>
                            <span className="text-xs text-[#A8A29E]">({formatBytes(file.size_bytes)})</span>
                          </div>
                        ))}
                      </div>
                    )
                  ))}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col gap-3">
                <Button onClick={() => setStep(2)} variant="outline" className="rounded-full gap-2" data-testid="cart-back-step2-btn"><ArrowLeft className="w-4 h-4" /> Back to Site Details</Button>
                <Button onClick={() => handleSubmit('rfq')} disabled={submitting} className="rounded-full bg-[#0061DD] hover:bg-[#004BB3] text-white px-8 py-5 text-lg font-semibold gap-2" data-testid="submit-rfq-btn">
                  {submitting ? 'Submitting...' : 'Submit RFQ'}
                </Button>
                {allBuyEligible && (
                  <Button onClick={() => handleSubmit('checkout')} disabled={submitting} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-5 text-lg font-semibold gap-2" data-testid="proceed-checkout-btn">
                    Proceed to Checkout
                  </Button>
                )}
              </div>

              {/* Share Cart */}
              <div className="bento-card p-5 mt-6" data-testid="cart-share-section">
                <h3 className="text-sm font-semibold text-[#1A1A1A] mb-3">Share this cart</h3>
                <p className="text-xs text-[#57534E] mb-3">Send your selections to a colleague for review before submitting.</p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="rounded-full text-xs gap-1.5"
                    onClick={() => {
                      const summary = cart.items.map(i => `${i.product_name} (${i.mode}) x${i.quantity}`).join('\n');
                      const text = `Voltide Quote Cart:\n${summary}\n\nView products: ${window.location.origin}/products`;
                      navigator.clipboard.writeText(text);
                      toast.success('Cart summary copied to clipboard');
                    }}
                    data-testid="cart-copy-link-btn"
                  >
                    <Copy className="w-3.5 h-3.5" /> Copy Summary
                  </Button>
                  <Button
                    variant="outline"
                    className="rounded-full text-xs gap-1.5"
                    onClick={() => {
                      const summary = cart.items.map(i => `- ${i.product_name} (${i.mode}) x${i.quantity} — ${i.pcs_kw} kW / ${i.battery_kwh} kWh`).join('%0A');
                      const subject = encodeURIComponent('Voltide BESS Quote Cart');
                      const body = `Hi,%0A%0AHere's my Voltide BESS selection:%0A%0A${summary}%0A%0AView products: ${window.location.origin}/products%0A%0ABest regards`;
                      window.open(`mailto:?subject=${subject}&body=${body}`);
                    }}
                    data-testid="cart-email-btn"
                  >
                    <Mail className="w-3.5 h-3.5" /> Email Cart
                  </Button>
                  <Button
                    variant="outline"
                    className="rounded-full text-xs gap-1.5"
                    onClick={async () => {
                      const summary = cart.items.map(i => `${i.product_name} (${i.mode}) x${i.quantity}`).join('\n');
                      const text = `Voltide Quote Cart:\n${summary}`;
                      if (navigator.share) {
                        await navigator.share({ title: 'Voltide Quote Cart', text, url: `${window.location.origin}/products` });
                      } else {
                        navigator.clipboard.writeText(text);
                        toast.success('Cart summary copied');
                      }
                    }}
                    data-testid="cart-share-btn"
                  >
                    <Share2 className="w-3.5 h-3.5" /> Share
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
